import axios, { AxiosInstance } from 'axios';

export interface PostackCMSConfig {
  baseUrl: string;
  apiKey?: string;
  token?: string;
}

export interface PostackCMSOptions {
  timeout?: number;
  headers?: Record<string, string>;
}

export interface Post {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt?: string;
  image?: string;
  featuredImage?: string;
  featured: boolean;
  published: boolean;
  createdAt: string;
  updatedAt: string;
  author?: {
    id: string;
    name: string;
    email: string;
  };
  category?: {
    id: string;
    name: string;
    slug: string;
  };
  tags?: Array<{
    id: string;
    name: string;
    slug: string;
  }>;
}

export interface PostsResponse {
  posts: Post[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface GetPostsOptions {
  published?: boolean;
  featured?: boolean;
  limit?: number;
  page?: number;
  category?: string;
  tag?: string;
  author?: string;
}

export class PostackCMS {
  private client: AxiosInstance;
  private config: PostackCMSConfig;

  constructor(config: PostackCMSConfig, options: PostackCMSOptions = {}) {
    this.config = config;
    this.client = axios.create({
      baseURL: config.baseUrl,
      timeout: options.timeout || 30000,
      headers: {
        'Content-Type': 'application/json',
        ...(config.apiKey && { 'Authorization': `Bearer ${config.apiKey}` }),
        ...(config.token && { Authorization: `Bearer ${config.token}` }),
        ...options.headers,
      },
    });
  }

  // Posts API methods
  async getPosts(options: GetPostsOptions = {}): Promise<PostsResponse> {
    const response = await this.client.get('/api/sdk/v1/posts', { params: options });
    return response.data;
  }

  async getPost(slug: string): Promise<Post> {
    const response = await this.client.get(`/api/sdk/v1/posts/${slug}`);
    return response.data;
  }

  async getFeaturedPosts(limit: number = 3): Promise<PostsResponse> {
    return this.getPosts({ featured: true, published: true, limit });
  }

  async getPostsByCategory(categorySlug: string, options: GetPostsOptions = {}): Promise<PostsResponse> {
    return this.getPosts({ ...options, category: categorySlug, published: true });
  }

  async getPostsByTag(tagSlug: string, options: GetPostsOptions = {}): Promise<PostsResponse> {
    return this.getPosts({ ...options, tag: tagSlug, published: true });
  }

  async getPostsByAuthor(authorId: string, options: GetPostsOptions = {}): Promise<PostsResponse> {
    return this.getPosts({ ...options, author: authorId, published: true });
  }

  // Authentication methods
  async login(email: string, password: string) {
    const response = await this.client.post('/auth/login', { email, password });
    return response.data;
  }

  async logout() {
    const response = await this.client.post('/auth/logout');
    return response.data;
  }

  async validateApiKey(apiKey: string) {
    const response = await this.client.post('/api/auth/validate-key', { apiKey });
    return response.data;
  }

  // Content methods (legacy - keeping for backward compatibility)
  async getContent(contentId: string) {
    const response = await this.client.get(`/content/${contentId}`);
    return response.data;
  }

  async createContent(data: any) {
    const response = await this.client.post('/content', data);
    return response.data;
  }

  async updateContent(contentId: string, data: any) {
    const response = await this.client.put(`/content/${contentId}`, data);
    return response.data;
  }

  async deleteContent(contentId: string) {
    const response = await this.client.delete(`/content/${contentId}`);
    return response.data;
  }

  // Media methods
  async uploadMedia(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.client.post('/media/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async getMedia(mediaId: string) {
    const response = await this.client.get(`/media/${mediaId}`);
    return response.data;
  }

  // User management methods
  async getCurrentUser() {
    const response = await this.client.get('/user/me');
    return response.data;
  }

  async updateUser(userId: string, data: any) {
    const response = await this.client.put(`/user/${userId}`, data);
    return response.data;
  }

  // Settings methods
  async getSettings() {
    const response = await this.client.get('/settings');
    return response.data;
  }

  async updateSettings(data: any) {
    const response = await this.client.put('/settings', data);
    return response.data;
  }
} 